import React, { useState } from 'react';
import { AssetStatus } from '../types';

interface Props {
  assets: AssetStatus[];
  onDownload: (assetId: string, assetName: string, imageUrl: string) => void;
  onDownloadAll: () => void;
  selectedAssets?: Set<string>;
  onAssetSelect?: (assetId: string) => void;
}

const AssetGallery: React.FC<Props> = ({
  assets,
  onDownload,
  onDownloadAll,
  selectedAssets: externalSelectedAssets,
  onAssetSelect
}) => {
  const [internalSelectedAssets, setInternalSelectedAssets] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<string>('all');

  // 使用外部传入的选择状态，如果没有则使用内部状态
  const selectedAssets = externalSelectedAssets || internalSelectedAssets;
  const setSelectedAssets = onAssetSelect ?
    (newSelected: Set<string>) => {
      newSelected.forEach(id => onAssetSelect(id));
    } :
    setInternalSelectedAssets;

  const completedAssets = assets.filter(asset => asset.status === 'completed' && asset.imageUrl);

  const filteredAssets = completedAssets.filter(asset => {
    if (filter === 'all') return true;
    return asset.assetType === filter;
  });

  const handleSelectAsset = (assetId: string) => {
    if (onAssetSelect) {
      onAssetSelect(assetId);
    } else {
      const newSelected = new Set(selectedAssets);
      if (newSelected.has(assetId)) {
        newSelected.delete(assetId);
      } else {
        newSelected.add(assetId);
      }
      setInternalSelectedAssets(newSelected);
    }
  };

  const handleSelectAll = () => {
    if (onAssetSelect) {
      // 外部控制模式，通知每个资源的选择状态变化
      filteredAssets.forEach(asset => onAssetSelect(asset.assetId));
    } else {
      // 内部控制模式
      if (selectedAssets.size === filteredAssets.length) {
        setInternalSelectedAssets(new Set());
      } else {
        setInternalSelectedAssets(new Set(filteredAssets.map(asset => asset.assetId)));
      }
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'character':
        return '👤';
      case 'background':
        return '🖼️';
      case 'item':
        return '🎯';
      case 'ui':
        return '🎨';
      default:
        return '📎';
    }
  };

  const getTypeName = (type: string) => {
    switch (type) {
      case 'character':
        return '角色';
      case 'background':
        return '背景';
      case 'item':
        return '道具';
      case 'ui':
        return '界面';
      default:
        return '其他';
    }
  };

  if (completedAssets.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 text-center">
        <div className="text-gray-400 mb-4">
          <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-800 mb-2">暂无完成的素材</h3>
        <p className="text-gray-600">素材生成完成后将在这里显示</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {/* 头部 */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4 sm:mb-0">
          🖼️ 素材画廊 ({completedAssets.length})
        </h2>

        {/* 操作按钮 */}
        <div className="flex space-x-2">
          <button
            onClick={onDownloadAll}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            📥 下载全部
          </button>
        </div>
      </div>

      {/* 筛选器 */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1 rounded-full text-sm transition-colors ${
            filter === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          全部 ({completedAssets.length})
        </button>
        {['character', 'background', 'item', 'ui'].map(type => {
          const count = completedAssets.filter(asset => asset.assetType === type).length;
          if (count === 0) return null;
          return (
            <button
              key={type}
              onClick={() => setFilter(type)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                filter === type
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {getTypeIcon(type)} {getTypeName(type)} ({count})
            </button>
          );
        })}
      </div>

      {/* 批量操作 */}
      {filteredAssets.length > 0 && (
        <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 rounded-lg">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={selectedAssets.size === filteredAssets.length && filteredAssets.length > 0}
              onChange={handleSelectAll}
              className="mr-2 rounded"
            />
            <span className="text-sm text-gray-700">
              {selectedAssets.size > 0 ? `已选择 ${selectedAssets.size} 项` : '全选'}
            </span>
          </label>
          {selectedAssets.size > 0 && (
            <button className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors">
              下载选中项
            </button>
          )}
        </div>
      )}

      {/* 素材网格 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredAssets.map((asset) => (
          <div key={asset.assetId} className="group relative">
            {/* 选择框 */}
            <div className="absolute top-2 left-2 z-10">
              <input
                type="checkbox"
                checked={selectedAssets.has(asset.assetId)}
                onChange={() => handleSelectAsset(asset.assetId)}
                className="rounded"
              />
            </div>

            {/* 素材卡片 */}
            <div className="bg-white border rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
              {/* 图片容器 */}
              <div className="relative aspect-square bg-gray-100">
                <img
                  src={asset.imageUrl}
                  alt={asset.assetName}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"><rect width="200" height="200" fill="%23f3f4f6"/><text x="100" y="100" text-anchor="middle" dy="0.3em" font-family="sans-serif" font-size="14" fill="%236b7280">图片加载失败</text></svg>';
                  }}
                />

                {/* 悬停操作 */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <button
                    onClick={() => asset.imageUrl && onDownload(asset.assetId, asset.assetName, asset.imageUrl)}
                    className="px-4 py-2 bg-white text-gray-800 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    📥 下载
                  </button>
                </div>
              </div>

              {/* 信息区域 */}
              <div className="p-4">
                <div className="flex items-center mb-2">
                  <span className="text-lg mr-2">{getTypeIcon(asset.assetType)}</span>
                  <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                    {getTypeName(asset.assetType)}
                  </span>
                </div>
                <h3 className="font-medium text-gray-800 mb-1 line-clamp-2">{asset.assetName}</h3>
                {asset.completedAt && (
                  <p className="text-xs text-gray-500">
                    {new Date(asset.completedAt).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 空状态 */}
      {filteredAssets.length === 0 && filter !== 'all' && (
        <div className="text-center py-8">
          <p className="text-gray-600">该类型暂无完成的素材</p>
        </div>
      )}
    </div>
  );
};

export default AssetGallery;